import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { Modal } from "@/shared/components/Modal";
import { CampoTexto } from "@/modules/visitantes/components/Campos";
import {
  MODELOS_WHATSAPP,
  formatarTelefone,
  linkWhatsapp,
  primeiroNome,
} from "@/modules/visitantes/utils/regras";

interface Props {
  aberto: boolean;
  /** Nome de quem vai receber a mensagem (a própria pessoa ou o responsável). */
  nomeDestino: string;
  telefone: string;
  /** Quando o destino é o responsável: menciona o visitante na mensagem. */
  nomeVisitante?: string;
  onClose: () => void;
  /** Chamado ao abrir o WhatsApp, para perguntar "Você conseguiu falar?". */
  onAbriu: () => void;
}

/** Escolhe um modelo, edita a mensagem e abre o WhatsApp (wa.me) — só dentro da ficha. */
export function ModalWhatsapp({
  aberto,
  nomeDestino,
  telefone,
  nomeVisitante,
  onClose,
  onAbriu,
}: Props) {
  const montar = (chave: (typeof MODELOS_WHATSAPP)[number]["chave"]) => {
    const modelo = MODELOS_WHATSAPP.find((m) => m.chave === chave) ?? MODELOS_WHATSAPP[0];
    const base = modelo.texto(primeiroNome(nomeDestino));
    return nomeVisitante ? `${base} (sobre ${primeiroNome(nomeVisitante)})` : base;
  };
  const [modelo, setModelo] = useState<(typeof MODELOS_WHATSAPP)[number]["chave"]>("boas_vindas");
  const [texto, setTexto] = useState("");

  useEffect(() => {
    if (aberto) {
      setModelo("boas_vindas");
      setTexto(montar("boas_vindas"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, nomeDestino, nomeVisitante]);

  function escolher(chave: (typeof MODELOS_WHATSAPP)[number]["chave"]) {
    setModelo(chave);
    setTexto(montar(chave));
  }

  function abrir() {
    window.open(linkWhatsapp(telefone, texto), "_blank", "noopener,noreferrer");
    onAbriu();
    onClose();
  }

  return (
    <Modal titulo="Enviar WhatsApp" aberto={aberto} onClose={onClose}>
      <p className="mb-3 text-sm text-muted">
        Para {nomeDestino} · {formatarTelefone(telefone)}
      </p>
      <div role="group" aria-label="Modelo de mensagem" className="mb-3 flex flex-wrap gap-2">
        {MODELOS_WHATSAPP.map((m) => (
          <button
            key={m.chave}
            type="button"
            onClick={() => escolher(m.chave)}
            aria-pressed={modelo === m.chave}
            className={`min-h-9 rounded-full border px-3 text-[13px] font-semibold transition-colors ${
              modelo === m.chave
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-text hover:bg-surface-2"
            }`}
          >
            {m.rotulo}
          </button>
        ))}
      </div>
      <CampoTexto
        label="Mensagem (pode editar)"
        name="mensagemWhatsapp"
        rows={5}
        maxLength={1000}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
      />
      <div className="mt-4 flex justify-end gap-2">
        <BotaoVoltar onClick={onClose} className="min-h-11" />
        <Button
          type="button"
          onClick={abrir}
          disabled={texto.trim() === ""}
          className="inline-flex min-h-11 items-center gap-1.5"
        >
          <MessageCircle size={16} aria-hidden="true" /> Abrir WhatsApp
        </Button>
      </div>
    </Modal>
  );
}
